import {
	claimMarketRetry,
	getDismissalKey,
	isAlertDismissed,
	markAlertDismissed,
} from '@/components/content/Header/parts/LocationAlert/utils/locationAlertDismissal';
import { useStoreLocatorState } from '@/data/state/useStoreLocatorState';
import {
	getIsAutoGeolocationPending,
	notifyLocationAlertChange,
	onLocationAlertChange,
} from '@/utils/locationAlertEvents';
import { findMarketIdByStLocId } from '@/utils/storeLocatorUtils';
import { useCurrentPhysicalStoreId } from '@/utils/useCurrentPhysicalStoreId';
import { useDeliveryMarket } from '@/utils/useDeliveryMarket';
import { useCallback, useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import {
	DELIVERY_LOCATION_COOKIE_NAME,
	DELIVERY_MARKET_COOKIE_NAME,
} from 'shared/custom/constants/commonConstants';
import {
	getDeliveryLocationCookie,
	getDeliveryMarketCookie,
} from 'shared/custom/utils/cookieHandler';

export type LocationAlertVariant = 'location' | 'store';

const resolvingZipCodes = new Set<string>();

export const useLocationAlert = (variant: LocationAlertVariant) => {
	useCookies([DELIVERY_LOCATION_COOKIE_NAME, DELIVERY_MARKET_COOKIE_NAME]);
	const { storeLocator } = useStoreLocatorState();
	const { physicalStoreId: cookieStoreId } = useCurrentPhysicalStoreId();
	const { resolveDeliveryMarket } = useDeliveryMarket();
	const [, setRevision] = useState(0);
	const [isMounted, setIsMounted] = useState(false);

	useEffect(() => {
		setIsMounted(true);
		return onLocationAlertChange(() => setRevision((revision) => revision + 1));
	}, []);

	const deliveryLocation = isMounted ? getDeliveryLocationCookie() : undefined;
	const customerZipCode = deliveryLocation?.setByCustomer ? deliveryLocation.zipCode : '';
	const deliveryMarket = isMounted ? getDeliveryMarketCookie() : undefined;
	const isMarketCurrent = !!customerZipCode && deliveryMarket?.zipCode === customerZipCode;
	const selectedStoreId = cookieStoreId ?? storeLocator?.selectedStore?.id ?? '';
	const selectedMarketId = findMarketIdByStLocId(selectedStoreId);

	let activeVariant: LocationAlertVariant | null = null;
	if (isMarketCurrent && deliveryMarket) {
		if (deliveryMarket.source !== 'proximity') {
			activeVariant = 'location';
		} else if (selectedMarketId && deliveryMarket.marketId !== selectedMarketId) {
			activeVariant = 'store';
		}
	}

	const dismissalKey = getDismissalKey(selectedStoreId, customerZipCode);
	const isDismissed = isMounted && isAlertDismissed(dismissalKey);

	const marketZipCode = deliveryMarket?.zipCode;
	const marketSource = deliveryMarket?.source;
	useEffect(() => {
		if (!isMounted || !customerZipCode || resolvingZipCodes.has(customerZipCode)) return;
		const isMissingOrStale = marketZipCode !== customerZipCode;
		const shouldRetry =
			!isMissingOrStale &&
			marketSource === 'geocodeUnavailable' &&
			claimMarketRetry(customerZipCode);
		if (!isMissingOrStale && !shouldRetry) return;

		resolvingZipCodes.add(customerZipCode);
		resolveDeliveryMarket(customerZipCode).finally(() => {
			resolvingZipCodes.delete(customerZipCode);
			notifyLocationAlertChange();
		});
	}, [isMounted, customerZipCode, marketZipCode, marketSource, resolveDeliveryMarket]);

	const dismiss = useCallback(() => {
		if (!activeVariant) return;
		markAlertDismissed(dismissalKey);
		notifyLocationAlertChange();
	}, [activeVariant, dismissalKey]);

	const isSettling = getIsAutoGeolocationPending();

	return { isVisible: activeVariant === variant && !isDismissed && !isSettling, dismiss };
};
